import { useEffect, useState, useRef } from "react";
import type { FormProps } from "antd";
import { Button, Form, Input, message, Segmented } from "antd";

import { useAPI } from "../../../hooks/useAPI";
import { postCall, updateCall } from "../../../constants/apicalls.constant";
import { ENDPOINTS } from "../../../constants/endpoints.constant";
import { ROUTES } from "../../../constants/routes.constant";
import { User } from "../../../models/user";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";

type LoginMethod = "email" | "phone";

type LoginFieldType = {
  identifier: string;
  otp?: string;
};

type SignupFieldType = {
  first_name: string;
  last_name: string;
  phone?: string;
  email?: string;
  organization_name: string;
};

const LoginForm = () => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user);

  const { loading, error, trigger } = useAPI<User>(ENDPOINTS.generate_otp);
  const { trigger: signupTrigger, loading: signupLoading } = useAPI<User>(
    ENDPOINTS.update_user,
  );
  const { loading: loginLoading } = useAPI<User>(ENDPOINTS.login);

  // Login state
  const [loginMethod, setLoginMethod] = useState<LoginMethod>("email");
  const [sendForVerification, setSendForVerification] = useState(false);
  const [loginIdentifier, setLoginIdentifier] = useState<string | null>(null);

  // Signup state
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [signupData, setSignupData] = useState<SignupFieldType | null>(null);
  const [signupOtpSent, setSignupOtpSent] = useState(false);
  const [signupContact, setSignupContact] = useState<string | null>(null);

  const [count, setCount] = useState(30);
  const [form] = Form.useForm();
  const intervalRef = useRef<number | null>(null);
  const otpVerifyingRef = useRef(false);

  useEffect(() => {
    if (user?.is_login && user?.status === "draft") {
      setShowSignupForm(true);
      if (user.email && !user.phone) {
        setLoginMethod("email");
      } else if (user.phone && !user.email) {
        setLoginMethod("phone");
      }
      form.setFieldsValue({
        first_name: user.first_name,
        last_name: user.last_name,
        organization_name:
          user.organization?.legal_name || user.legal_name || "",
        email: user.email,
        phone: user.phone,
      });
    }
  }, [user, form]);

  // ================= TIMER =================

  const startTimer = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setCount(30);
    intervalRef.current = window.setInterval(() => {
      setCount((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // ================= LOGIN FLOW =================

  const onLoginFinish: FormProps<LoginFieldType>["onFinish"] = async (
    values,
  ) => {
    if (sendForVerification) {
      await onVerifyLoginOTP(values.otp!);
    } else {
      await onSendLoginOTP(values.identifier);
    }
  };

  const onSendLoginOTP = async (identifier: string) => {
    const payload =
      loginMethod === "email" ? { email: identifier } : { phone: identifier };

    const res = await trigger(postCall(payload));

    if (res?.status) {
      message.success(`OTP sent to ${loginMethod}`);
      setLoginIdentifier(identifier);
      setSendForVerification(true);
      startTimer();
    } else {
      if (res?.errors) {
        const fieldErrors = Object.keys(res.errors).map((field) => ({
          name: field === "email" || field === "phone" ? "identifier" : field,
          errors: res.errors?.[field] || [],
        }));
        form.setFields(fieldErrors);
      }
      message.error(res?.message);
    }
  };

  const onVerifyLoginOTP = async (otp: string) => {
    const payload =
      loginMethod === "email"
        ? { email: loginIdentifier, otp }
        : { phone: loginIdentifier, otp };

    const res = await trigger(postCall(payload, {}, ENDPOINTS.login));

    if (res?.status) {
      if (res?.data?.status === "draft") {
        setShowSignupForm(true);
      } else {
        navigate(ROUTES.LIST_EVENT.path);
      }
    } else {
      if (res?.errors) {
        const fieldErrors = Object.keys(res.errors).map((field) => ({
          name: field,
          errors: res.errors?.[field] || [],
        }));
        form.setFields(fieldErrors);
      }
      message.error(res?.message);
    }
  };

  const onResendLoginOTP = async () => {
    await onSendLoginOTP(loginIdentifier!);
  };

  // ================= SIGNUP FLOW =================

  /**
   * If user logged in via email  → signup form collects phone → OTP sent to phone
   * If user logged in via phone  → signup form collects email → OTP sent to email
   */
  const onSignupFinish: FormProps<SignupFieldType>["onFinish"] = async (
    values,
  ) => {
    setSignupData(values);

    const payload = {
      first_name: values.first_name,
      last_name: values.last_name,
      organization_name: values.organization_name,
      userVerification: true,
      ...(loginMethod === "email"
        ? { phone: values.phone }
        : { email: values.email }),
    };

    const res = await trigger(postCall(payload));

    if (res?.status) {
      const target =
        loginMethod === "email" ? `+91 ${values.phone}` : values.email!;
      message.success(
        `OTP sent to ${loginMethod === "email" ? "mobile" : "email"}`,
      );
      setSignupContact(target);
      setSignupOtpSent(true);
      startTimer();
    } else {
      if (res?.errors) {
        const fieldErrors = Object.keys(res.errors).map((field) => ({
          name: field,
          errors: res.errors?.[field] || [],
        }));
        form.setFields(fieldErrors);
      }
      message.error(res?.message);
    }
  };

  const onVerifySignupOTP = async (otp: string) => {
    if (otp.length < 6 || otpVerifyingRef.current) return;
    otpVerifyingRef.current = true;

    const payload = {
      otp,
      first_name: signupData?.first_name,
      last_name: signupData?.last_name,
      organization_name: signupData?.organization_name,
      ...(loginMethod === "email"
        ? { phone: signupData?.phone }
        : { email: signupData?.email }),
    };

    const updateRes = await signupTrigger(
      updateCall("", payload, {}, ENDPOINTS.update_user),
    );

    if (updateRes?.status) {
      message.success("Signup completed!");
      navigate(ROUTES.LIST_EVENT.path);
    } else {
      otpVerifyingRef.current = false;
      if (updateRes?.errors) {
        const fieldErrors = Object.keys(updateRes.errors).map((field) => ({
          name: field,
          errors: updateRes.errors?.[field] || [],
        }));
        form.setFields(fieldErrors);
      }
      message.error(updateRes?.message ?? "Failed to save profile");
    }
  };

  const onResendSignupOTP = async () => {
    const payload =
      loginMethod === "email"
        ? { phone: signupData?.phone }
        : { email: signupData?.email };

    const res = await trigger(postCall(payload));
    if (res?.status) {
      message.success("OTP resent");
      startTimer();
    } else {
      if (res?.errors) {
        const fieldErrors = Object.keys(res.errors).map((field) => ({
          name: field,
          errors: res.errors?.[field] || [],
        }));
        form.setFields(fieldErrors);
      }
      message.error(res?.message);
    }
  };

  const isSubmitting = loading || signupLoading || loginLoading;

  const formatResendTimer = (seconds: number) =>
    `00:${String(seconds).padStart(2, "0")}`;

  if (error) return <>Something went wrong...</>;

  return (
    <>
      {!showSignupForm ? (
        // ================= LOGIN FORM =================
        <Form
          form={form}
          onFinish={onLoginFinish}
          layout="vertical"
          className="w-full!"
        >
          {!sendForVerification && (
            <>
              <Form.Item className="mb-4">
                <Segmented
                  options={[
                    { label: "Email", value: "email" },
                    { label: "Phone", value: "phone" },
                  ]}
                  value={loginMethod}
                  onChange={(val) => {
                    setLoginMethod(val as LoginMethod);
                    form.resetFields(["identifier"]);
                  }}
                  block
                />
              </Form.Item>

              {loginMethod === "email" ? (
                <Form.Item
                  label="Email"
                  name="identifier"
                  rules={[
                    { required: true },
                    { type: "email", message: "Enter valid email" },
                  ]}
                >
                  <Input placeholder="Enter email" />
                </Form.Item>
              ) : (
                <Form.Item
                  label="Mobile"
                  name="identifier"
                  rules={[
                    { required: true, message: "Phone number is required!" },
                    {
                      pattern: /\d{10}$/,
                      message: "Enter a valid 10-digit Indian mobile number",
                    },
                  ]}
                >
                  <Input
                    maxLength={10}
                    prefix="+91"
                    placeholder="Enter mobile number"
                  />
                </Form.Item>
              )}
            </>
          )}

          {sendForVerification && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  OTP sent to{" "}
                  <strong>
                    {loginMethod === "phone"
                      ? `+91 ${loginIdentifier}`
                      : loginIdentifier}
                  </strong>{" "}
                </p>
                <Button
                  type="link"
                  size="small"
                  className="p-0 h-auto underline"
                  onClick={() => {
                    setSendForVerification(false);
                    form.setFieldValue("otp", "");
                  }}
                >
                  Change
                </Button>
              </div>
              <Form.Item
                label="OTP"
                name="otp"
                rules={[
                  { required: true, message: "Please enter OTP" },
                  {
                    pattern: /^\d{6}$/,
                    message: "Please enter a valid 6-digit OTP",
                  },
                ]}
              >
                <Input.OTP
                  onChange={(value) => {
                    if (value.length === 6) form.submit();
                  }}
                />
              </Form.Item>

              <div className="text-end mb-4">
                {count === 0 ? (
                  <button
                    type="button"
                    onClick={onResendLoginOTP}
                    className="underline cursor-pointer bg-transparent border-0 p-0 text-inherit"
                  >
                    Resend
                  </button>
                ) : (
                  <span>Resend in {formatResendTimer(count)}</span>
                )}
              </div>
            </>
          )}

          <Button type="primary" htmlType="submit" block loading={isSubmitting}>
            {sendForVerification ? "Verify OTP" : "Get OTP"}
          </Button>
        </Form>
      ) : (
        // ================= SIGNUP FORM =================
        <Form form={form} layout="vertical" onFinish={onSignupFinish}>
          <h2 className="text-xl font-semibold mb-4">Complete your profile</h2>

          {!signupOtpSent ? (
            <>
              <Form.Item
                name="first_name"
                label="First Name"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="last_name"
                label="Last Name"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>

              {loginMethod === "email" ? (
                // Logged in via email → collect phone
                <Form.Item
                  name="phone"
                  label="Mobile"
                  rules={[
                    { required: true, message: "Phone number is required!" },
                    {
                      pattern: /\d{10}$/,
                      message: "A valid 10 digit Indian phone number please!",
                    },
                  ]}
                >
                  <Input maxLength={10} prefix="+91" autoFocus />
                </Form.Item>
              ) : (
                // Logged in via phone → collect email
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: "Email is required!" },
                    { type: "email", message: "Enter a valid email address" },
                  ]}
                >
                  <Input placeholder="Enter email" autoFocus />
                </Form.Item>
              )}

              <Form.Item
                name="organization_name"
                label="Organization"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                block
                loading={isSubmitting}
              >
                Continue
              </Button>
            </>
          ) : (
            <>
              <p className="mb-4 text-sm text-gray-500">
                OTP sent to <strong>{signupContact}</strong>
              </p>

              <Form.Item
                label={`OTP sent to ${loginMethod === "email" ? "mobile" : "email"}`}
                name="signup_otp"
                rules={[
                  { required: true, message: "Please enter OTP" },
                  {
                    pattern: /^\d{6}$/,
                    message: "Please enter a valid 6-digit OTP",
                  },
                ]}
              >
                <Input.OTP
                  onChange={(val) => {
                    if (val.length === 6) onVerifySignupOTP(val);
                  }}
                />
              </Form.Item>

              <div className="text-end mb-4">
                {count === 0 ? (
                  <button
                    type="button"
                    onClick={onResendSignupOTP}
                    className="underline cursor-pointer bg-transparent border-0 p-0 text-inherit"
                  >
                    Resend
                  </button>
                ) : (
                  <span>Resend in {formatResendTimer(count)}</span>
                )}
              </div>

              <Button onClick={() => setSignupOtpSent(false)}>
                {loginMethod === "email" ? "Change Number" : "Change Email"}
              </Button>
            </>
          )}

          <Button
            type="link"
            onClick={() => {
              setShowSignupForm(false);
              setSendForVerification(false);
              setLoginIdentifier(null);
              setSignupOtpSent(false);
              form.resetFields();
            }}
            className="mt-2"
          >
            Back to Login
          </Button>
        </Form>
      )}
    </>
  );
};

export default LoginForm;
